import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { lambda, getAuthToken, API_USER_AGENT, products as productsCache } from '../Functions';
import { onAuthStateChanged } from '../cognito';
import {
  cartItemProviderId,
  getAddToCartProviderConflict,
  normalizeProviderId,
} from '../utils/cartProvider';
import { ADD_TO_CART_ENABLED } from '../data/featureFlags';

const CartContext = createContext();

function resolveVoucherProviderId(voucher, matchingLocalItem, productFromCache) {
  if (voucher != null && voucher.provider_id != null) {
    return normalizeProviderId(voucher);
  }
  if (matchingLocalItem) return cartItemProviderId(matchingLocalItem);
  if (productFromCache) return normalizeProviderId(productFromCache);
  return normalizeProviderId(null);
}

/** Guest cart is stored in sessionStorage (cleared when tab closes). Logged-in cart is API-only. */
const CART_STORAGE_KEY = 'giftbox_cart';

function getStoredCart() {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function setStoredCart(items) {
  try {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(Array.isArray(items) ? items : []));
  } catch (_e) {
  }
}

/** Drop duplicate voucher rows if the API returns the same voucher_id twice (e.g. after merge/transfer). */
function dedupeVouchersById(vouchers) {
  if (!Array.isArray(vouchers) || vouchers.length === 0) return vouchers || [];
  const seen = new Set();
  const out = [];
  for (const v of vouchers) {
    const vid = v.voucher_id ?? v.id;
    if (vid != null && vid !== '') {
      const key = String(vid);
      if (seen.has(key)) continue;
      seen.add(key);
    }
    out.push(v);
  }
  return out;
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const cartItemsRef = React.useRef([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(true);
  const processCartDataRef = React.useRef(() => {});
  const loadCartFromServerRef = React.useRef(() => {});

  // Load cart from MySQL on mount (if user is logged in)
  useEffect(() => {
    const loadCart = async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          // User not logged in - load from sessionStorage (guest cart)
          const parsed = getStoredCart();
          if (parsed.length > 0) {
            setCartItems(parsed);
            cartItemsRef.current = parsed;
          }
          setIsLoadingCart(false);
          return;
        }

        // User is logged in - load from MySQL
        const response = await fetch(`${lambda}/giftbox/cart`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'User-Agent': API_USER_AGENT,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.cart)) {
            const storedCart = getStoredCart();

            // Server cart empty but guest still has lines (e.g. just signed in, transfer not run yet) — do not wipe.
            if (data.cart.length === 0 && storedCart.length > 0) {
              const guestOnly = storedCart.every((x) => !x.voucher_id);
              if (guestOnly) {
                setCartItems(storedCart);
                cartItemsRef.current = storedCart;
                setIsLoadingCart(false);
                return;
              }
            }

            const vouchers = dedupeVouchersById(data.cart);

            // Convert MySQL vouchers to cart items format; merge with stored cart for product info
            const cartItemsFromDB = vouchers.map((voucher, index) => {
              const voucherId = voucher.voucher_id ?? voucher.id;
              const clientId = voucherId != null ? `voucher_${voucherId}` : `cart_${voucher.product_id}_${parseFloat(voucher.price)}_${index}`;

              const matchingLocalItem = storedCart.find(
                item => (item.product?.id || item.product_id) === voucher.product_id &&
                        (item.amount || item.product?.prices?.retail?.amount) === parseFloat(voucher.price)
              );

              // If we found a match, preserve the product object and other metadata
              if (matchingLocalItem && matchingLocalItem.product) {
                const provider_id = resolveVoucherProviderId(voucher, matchingLocalItem, null);
                return {
                  ...matchingLocalItem,
                  id: clientId,
                  voucher_id: voucherId ?? undefined,
                  quantity: voucher.quantity,
                  amount: parseFloat(voucher.price),
                  provider_id,
                  product: matchingLocalItem.product
                    ? { ...matchingLocalItem.product, provider_id }
                    : matchingLocalItem.product,
                };
              }

              // Use product_name and product_image from API if available
              if (voucher.product_name || voucher.product_image) {
                const provider_id = resolveVoucherProviderId(voucher, null, null);
                return {
                  id: clientId,
                  voucher_id: voucherId ?? undefined,
                  product_id: voucher.product_id,
                  provider_id,
                  product: {
                    id: voucher.product_id,
                    name: voucher.product_name || `Product #${voucher.product_id}`,
                    image: voucher.product_image || null,
                    provider_id,
                  },
                  quantity: voucher.quantity,
                  amount: parseFloat(voucher.price),
                };
              }

              // Otherwise, create minimal cart item
              const provider_id = resolveVoucherProviderId(voucher, null, null);
              return {
                id: clientId,
                voucher_id: voucherId ?? undefined,
                product_id: voucher.product_id,
                provider_id,
                quantity: voucher.quantity,
                amount: parseFloat(voucher.price),
              };
            });

            setCartItems(cartItemsFromDB);
            cartItemsRef.current = cartItemsFromDB;
            setStoredCart(cartItemsFromDB);
          }
        } else {
          const parsed = getStoredCart();
          if (parsed.length > 0) {
            setCartItems(parsed);
            cartItemsRef.current = parsed;
          }
        }
      } catch (_error) {
        const parsed = getStoredCart();
        if (parsed.length > 0) {
          setCartItems(parsed);
          cartItemsRef.current = parsed;
        }
      } finally {
        setIsLoadingCart(false);
      }
    };

    // Listen for auth state changes: transfer guest cart → reload server cart
    const unsubscribe = onAuthStateChanged(async (user) => {
      if (user) {
        await loadCart();
      } else {
        const parsed = getStoredCart();
        setCartItems(parsed);
        cartItemsRef.current = parsed;
        setIsLoadingCart(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Keep ref in sync; persist to sessionStorage only for guest cart (no voucher_id) so checkout can read it
  useEffect(() => {
    cartItemsRef.current = cartItems;
    const isGuestCart = cartItems.length === 0 || cartItems.some(item => !item.voucher_id);
    if (isGuestCart) {
      setStoredCart(cartItems);
    }
  }, [cartItems]);

  // Helper function to process cart data from server response
  const processCartData = (cartVouchers) => {
    if (!Array.isArray(cartVouchers)) {
      return;
    }

    const storedCart = getStoredCart();

    if (cartVouchers.length === 0) {
      const guestOnly = storedCart.length > 0 && storedCart.every((x) => !x.voucher_id);
      if (guestOnly) {
        setCartItems(storedCart);
        cartItemsRef.current = storedCart;
        return;
      }
      setCartItems([]);
      cartItemsRef.current = [];
      setStoredCart([]);
      return;
    }

    const deduped = dedupeVouchersById(cartVouchers);

    const cartItemsFromDB = deduped.map((voucher, index) => {
      let matchingLocalItem = storedCart.find(
        item => (item.product?.id || item.product_id) === voucher.product_id &&
                (item.amount || item.product?.prices?.retail?.amount) === parseFloat(voucher.price)
      );
      if (!matchingLocalItem || !matchingLocalItem.product) {
        matchingLocalItem = storedCart.find(
          item => (item.product?.id || item.product_id) === voucher.product_id
        );
      }

      // If still no match, try to find product in products cache
      let productFromCache = null;
      if (!matchingLocalItem || !matchingLocalItem.product) {
        // Search through all cached products
        for (const serviceName in productsCache) {
          const serviceProducts = productsCache[serviceName];
          if (Array.isArray(serviceProducts?.Products)) {
            productFromCache = serviceProducts.Products.find(
              p => p.id === voucher.product_id
            );
            if (productFromCache) break;
          }
        }
      }

      const voucherId = voucher.voucher_id ?? voucher.id;
      const clientId = voucherId != null ? `voucher_${voucherId}` : `cart_${voucher.product_id}_${parseFloat(voucher.price)}_${index}`;

      if (matchingLocalItem && matchingLocalItem.product) {
        const provider_id = resolveVoucherProviderId(voucher, matchingLocalItem, null);
        return {
          ...matchingLocalItem,
          id: clientId,
          voucher_id: voucherId ?? undefined,
          quantity: voucher.quantity,
          amount: parseFloat(voucher.price),
          provider_id,
          product: matchingLocalItem.product
            ? { ...matchingLocalItem.product, provider_id }
            : matchingLocalItem.product,
        };
      }

      // If we found product in cache, create cart item with product info
      if (productFromCache) {
        const provider_id = resolveVoucherProviderId(voucher, null, productFromCache);
        return {
          id: clientId,
          voucher_id: voucherId ?? undefined,
          product: { ...productFromCache, provider_id },
          product_id: voucher.product_id,
          provider_id,
          quantity: voucher.quantity,
          amount: parseFloat(voucher.price),
        };
      }

      // Use product_name and product_image from API if available
      if (voucher.product_name || voucher.product_image) {
        const provider_id = resolveVoucherProviderId(voucher, null, null);
        return {
          id: clientId,
          voucher_id: voucherId ?? undefined,
          product_id: voucher.product_id,
          provider_id,
          product: {
            id: voucher.product_id,
            name: voucher.product_name || `Product #${voucher.product_id}`,
            image: voucher.product_image || null,
            provider_id,
          },
          quantity: voucher.quantity,
          amount: parseFloat(voucher.price),
        };
      }

      // Otherwise, create minimal cart item (will show fallback in UI)
      const provider_id = resolveVoucherProviderId(voucher, null, null);
      return {
        id: clientId,
        voucher_id: voucherId ?? undefined,
        product_id: voucher.product_id,
        provider_id,
        quantity: voucher.quantity,
        amount: parseFloat(voucher.price),
      };
    });

    setCartItems(cartItemsFromDB);
    cartItemsRef.current = cartItemsFromDB;
    setStoredCart(cartItemsFromDB);
  };

  // Helper function to load cart from MySQL
  const loadCartFromServer = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        // User not logged in - skip server load
        return;
      }

      const response = await fetch(`${lambda}/giftbox/cart`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'User-Agent': API_USER_AGENT,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.cart)) {
          processCartData(data.cart);
        }
      }
    } catch (_error) {
    }
  };

  useEffect(() => {
    processCartDataRef.current = processCartData;
    loadCartFromServerRef.current = loadCartFromServer;
  });

  const addToCart = useCallback(async (product, quantity = 1, amount = null, serviceId = null, pid = null) => {
    if (!ADD_TO_CART_ENABLED) return { error: 'PURCHASING_PAUSED' };
    const price = amount || product.prices?.retail?.amount || 0;
    // Use pid from data-pid attribute if provided, otherwise use product.id
    const productId = pid || product.id;

    const conflict = getAddToCartProviderConflict({ ...product, id: productId }, cartItemsRef.current);
    if (conflict) {
      return conflict;
    }

    // Check if item already exists before updating
    const existingItemIndex = cartItemsRef.current.findIndex(
      item => {
        const itemProductId = item.product?.id || item.product_id;
        return itemProductId === productId && item.amount === amount;
      }
    );
    
    const isNewItem = existingItemIndex < 0;

    // When signed in: only API. When guest: only sessionStorage + state.
    try {
      const token = await getAuthToken();
      if (token) {
        const response = await fetch(`${lambda}/giftbox/cart/add`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'User-Agent': API_USER_AGENT,
          },
          body: JSON.stringify({
            product_id: productId, // Use pid from data-pid attribute
            quantity,
            provider_id: normalizeProviderId(product),
            ...(amount != null && !isNaN(Number(amount)) ? { price: Number(amount) } : {}),
            ...(typeof window !== 'undefined' && window.sessionStorage?.getItem('cart_phone') ? { phone: window.sessionStorage.getItem('cart_phone') } : {}),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            await loadCartFromServerRef.current();
            setIsDrawerOpen(true);
            return { success: true, isNewItem, product, quantity };
          }
        }
      }
    } catch (_error) {
    }

    // Guest or API failed: update state and sessionStorage only
    const prevItems = cartItemsRef.current;
    const existingIndex = prevItems.findIndex(
      item => {
        const itemProductId = item.product?.id || item.product_id;
        return itemProductId === productId && item.amount === amount;
      }
    );
    const nextItems = existingIndex >= 0
      ? (() => {
          const updated = [...prevItems];
          updated[existingIndex].quantity += quantity;
          return updated;
        })()
      : [...prevItems, (() => {
          const provider_id = normalizeProviderId(product);
          const productWithProvider = product && typeof product === 'object'
            ? { ...product, provider_id }
            : product;
          return {
            product: productWithProvider,
            product_id: productId,
            provider_id,
            quantity,
            amount,
            serviceId: serviceId || product.service_id || null,
            id: `${productId}_${amount || 'default'}_${Date.now()}`,
          };
        })()];
    setCartItems(nextItems);
    cartItemsRef.current = nextItems;
    setStoredCart(nextItems);
    setIsDrawerOpen(true);
    return { success: true, isNewItem, product, quantity };
  }, []);

  const removeFromCart = useCallback(async (itemId) => {
    const item = cartItemsRef.current.find(i => i.id === itemId);
    // Resolve voucher_id: from item or from id like "voucher_123"
    const voucherId = item?.voucher_id ?? (typeof itemId === 'string' && itemId.startsWith('voucher_') ? itemId.replace(/^voucher_/, '') : null);
    const hasVoucherId = voucherId != null && String(voucherId).trim() !== '';

    if (hasVoucherId) {
      try {
        const token = await getAuthToken();
        if (token) {
          const response = await fetch(`${lambda}/giftbox/cart/remove`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'User-Agent': API_USER_AGENT,
            },
            body: JSON.stringify({
              voucher_id: Number(voucherId) || voucherId,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success !== undefined && Array.isArray(data.cart)) {
              processCartDataRef.current(data.cart);
              return;
            }
          }
        }
      } catch (_error) {
      }
    }

    setCartItems(prevItems => prevItems.filter(i => i.id !== itemId));
    cartItemsRef.current = cartItemsRef.current.filter(i => i.id !== itemId);
    setStoredCart(cartItemsRef.current);
  }, []);

  const updateQuantity = useCallback(async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      await removeFromCart(itemId);
      return;
    }

    const item = cartItemsRef.current.find(item => item.id === itemId);
    
    if (item?.voucher_id) {
      try {
        const token = await getAuthToken();
        if (token) {
          const response = await fetch(`${lambda}/giftbox/cart/update`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'User-Agent': API_USER_AGENT,
            },
            body: JSON.stringify({
              voucher_id: item.voucher_id,
              quantity: newQuantity,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success !== undefined && Array.isArray(data.cart)) {
              processCartDataRef.current(data.cart);
            }
            return;
          }
        }
      } catch (_error) {
      }
    }

    const nextItems = cartItemsRef.current.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(nextItems);
    cartItemsRef.current = nextItems;
    setStoredCart(nextItems);
  }, []);

  const clearCart = useCallback(async () => {
    const itemsToRemove = cartItemsRef.current.filter(item => item.voucher_id);
    const token = await getAuthToken();

    if (itemsToRemove.length > 0 && token) {
      try {
        const removePromises = itemsToRemove.map(item =>
          fetch(`${lambda}/giftbox/cart/remove`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'User-Agent': API_USER_AGENT,
            },
            body: JSON.stringify({
              voucher_id: item.voucher_id,
            }),
          }).catch(() => {})
        );
        await Promise.all(removePromises);
        await loadCartFromServerRef.current();
      } catch (_error) {
      }
    }

    setCartItems([]);
    cartItemsRef.current = [];
    setStoredCart([]);
  }, []);

  /** Clear cart in state and sessionStorage only (e.g. after order created; no API call) */
  const clearCartLocalOnly = useCallback(() => {
    setCartItems([]);
    cartItemsRef.current = [];
    setStoredCart([]);
  }, []);

  /** Re-fetch cart from API (e.g. after server restores cart on failed checkout). */
  const refreshCart = useCallback(async () => {
    await loadCartFromServerRef.current();
  }, []);

  const getTotalItems = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  const getTotalPrice = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const price = item.amount || item.product?.prices?.retail?.amount || 0;
      return total + (price * item.quantity);
    }, 0);
  }, [cartItems]);

  const value = React.useMemo(() => ({
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearCartLocalOnly,
    refreshCart,
    getTotalItems,
    getTotalPrice,
    isDrawerOpen,
    setIsDrawerOpen,
    isLoadingCart,
  }), [cartItems, isDrawerOpen, isLoadingCart, addToCart, removeFromCart, updateQuantity, clearCart, clearCartLocalOnly, refreshCart, getTotalItems, getTotalPrice]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

